import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'next-i18next'
import { css } from '@emotion/react'
import shimmer from './shimmer'
import Image from 'next/image'
import { AppCurrencyConfig, DonationFormConfig } from '../../../config/state/config-types'
import { Frequency, FREQUENCY, FREQUENCY_LABELS } from '@classy/campaign-page-blocks'
import { OrderFrequencyValue } from 'modules/order/state/frequency/order-frequency-types'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import { convertHexToRGB, convertRgbToRGBA, isHexColor } from '../../../../utils/colors'
import FlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import { getBorderRadius, getBorderRadiusAmount } from 'utils/getBorderRadius'
import { SelectInput } from 'modules/ui/atoms/SelectInput'
import Row from 'modules/ui/atoms/Layout/Row'
import Column from 'modules/ui/atoms/Layout/Column'
import DateInput from 'modules/ui/atoms/DateInput'
import FrequencyToggleGroup from './FrequencyToggleGroup'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { OrderRecurringEndDateValue } from 'modules/order/state/recurring-end-date/order-recurring-end-date-types'
import { formatCurrency } from 'utils/functions/currencyUtils'
import useOrder from 'modules/order/state/order/use-order'
import { getCurrencyDecimalPlaces } from '../../../../utils/currency'
import { getFrequencyString } from 'utils/functions/frequencyUtils'

interface DonationSubmitDetail {
  frequency: string | null
  amount: number | null
}

interface DonationFormProps {
  config: DonationFormConfig
  onSubmit: (data: DonationSubmitDetail) => void
  onAmountChange?: (amount: number) => void
  onFrequencyChange?: (frequency: OrderFrequencyValue) => void
  onRecurringEndDateChange?: (recurringEndDate?: string) => void
  isLoadingAmounts: boolean
  currencies: AppCurrencyConfig
  initialRecurringEndDate?: string
  isIntelligentAsksEnabled: boolean | undefined
}

// Main container for the form
const DonationFormContainer = styled.div<{
  backgroundColor: string
  borderRadius: string
  boxShadow: string
}>`
  padding: 24px 0px 24px 0px;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 700px;
  min-width: 352px;
  width: 100%;
  background: ${({ backgroundColor }) => backgroundColor};
  border-radius: ${({ borderRadius }) => borderRadius};
  box-shadow: ${({ boxShadow }) => boxShadow};
  font-family: var(--classy-font__font-family--base, sans-serif);
`

// Wrapper for form content to stay at the top
const FormContentWrapper = styled.div`
  flex-grow: 0;
`

// Spacer to push the submit button to the bottom
const Spacer = styled.div`
  flex-grow: 1;
`

// Heading logo container
const HeadingLogo = styled.div`
  position: relative;
  text-align: center;
  height: 68px;
  margin: 20px 24px;
`

// Subheading styles
const SubHeading = styled.div`
  font-size: 16px;
  text-align: center;
  margin: 24px 0;
  font-weight: 500;
  span {
    font-weight: bold;
  }
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`

// Amount Selector Grid
const AmountSelectorContainer = styled.div<{ displayAmounts: number }>`
  display: grid;
  grid-template-columns: ${({ displayAmounts }) =>
    displayAmounts === 1 ? '1fr' : displayAmounts === 3 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'};
  gap: 4px;
  margin-bottom: 8px;
`

// Amount Button styles
const AmountButton = styled.button<{
  active: boolean
  borderRadius: string
  primaryColor: string
  secondaryColor: string
  isLoading: boolean
}>`
  padding: 16px;
  height: 56px;
  background-color: ${({ active, primaryColor, secondaryColor }) =>
    active
      ? primaryColor || 'var(--classy-custom-primary-color, #364fc7)'
      : secondaryColor || 'var(--classy-custom-secondary-color, #e8ebf9)'};
  color: ${({ active }) =>
    active
      ? 'var(--computed-color__brand-primary--contrast, #ffffff)'
      : DEFAULT_THEME_COLORS['--theme-color--neutral-10']};
  border: none;
  border-radius: ${({ borderRadius }) => borderRadius};
  font-size: 20px;
  font-weight: bold;
  line-height: 30px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({ active, primaryColor }) =>
      active ? primaryColor || 'var(--classy-custom-primary-color, #364fc7)' : '#dcdcdc'};
  }
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  ${({ isLoading, borderRadius, primaryColor }) =>
    isLoading &&
    css`
      position: relative;
      color: transparent;
      pointer-events: none;
      background-color: #dcdcdc;
      background-image: linear-gradient(
        90deg,
        ${convertRgbToRGBA(primaryColor, 0.1)} 20%,
        ${convertRgbToRGBA(primaryColor, 0.6)} 50%,
        ${convertRgbToRGBA(primaryColor, 0.1)} 80%
      );
      background-size: 400% 100%;
      animation: ${shimmer} 1.5s infinite linear;
      background-clip: padding-box;
      border-radius: ${borderRadius};
      box-sizing: border-box;
      line-height: 1;
    `}
`

// Custom Amount Input styles
const CustomAmountContainer = styled.div<{
  active: boolean
  error: boolean
  borderRadius: string
  primaryColor: string
  secondaryColor: string
}>`
  display: flex;
  align-items: center;
  height: 56px;
  border-radius: ${({ borderRadius }) => borderRadius};
  border: ${({ error }) => (error ? '2px solid var(--classy-color__status--error-base)' : 'none')};
  background-color: ${({ secondaryColor }) =>
    secondaryColor || 'var(--classy-custom-secondary-color, #e8ebf9)'};
  line-height: 30px;
  margin-bottom: 16px;
  outline: ${({ active, primaryColor }) =>
    active ? primaryColor || '2px solid var(--classy-custom-primary-color, #425cc7)' : 'none'};
  outline-offset: -2px;

  &:focus-within {
    border: ${({ primaryColor }) =>
      `2px solid ${primaryColor}` || '2px solid var(--classy-custom-primary-color, #425cc7)'};
  }
`

const CustomAmountInputContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  background-color: transparent;
  padding-left: 12px;
  border: none;
  font-family: var(--classy-font__font-family--base, sans-serif);
  font-size: var(--classy-font__paragraph-lg-bold--font-size, 20px);
  line-height: var(--classy-font__paragraph-lg-bold--line-height, 1.5);
  font-weight: var(--classy-font__paragraph-lg-bold--font-weight, 700);
`

const CustomAmountInput = styled.input`
  width: 100%;
  background-color: transparent;
  border: none;
  font-family: var(--classy-font__font-family--base, sans-serif);
  font-size: var(--classy-font__paragraph-lg-bold--font-size, 20px);
  line-height: 1.5;
  font-weight: 700;
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-10']};
  outline: none;

  &::placeholder {
    color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-10']};
    font-weight: 700;
  }
`

const ErrorMessage = styled.div`
  font-family: var(--classy-font__font-family--base);
  font-size: var(--classy-font__label--font-size);
  line-height: var(--classy-font__label--line-height);
  font-weight: var(--classy-font__label--font-weight);
  color: var(--classy-color__status--error-base);
  margin-top: 8px;
`

const FrequencyFieldset = styled.fieldset`
  margin-bottom: 16px;
  border: none;
  padding: 0;
`

const DonationForm: React.FC<DonationFormProps> = ({
  config,
  onSubmit,
  onAmountChange,
  onFrequencyChange,
  onRecurringEndDateChange,
  isLoadingAmounts,
  initialRecurringEndDate,
  currencies,
  isIntelligentAsksEnabled,
}) => {
  const order = useOrder()

  const { t, i18n } = useTranslation()
  const analytics = getAnalyticsInstance()
  const [selectedFrequency, setSelectedFrequency] = useState<Frequency>(order.frequency.value)
  const [selectedAmount, setSelectedAmount] = useState<number>(order.amount.value)

  const [recurringEndDate, setRecurringEndDate] =
    useState<OrderRecurringEndDateValue>(initialRecurringEndDate)

  const visibleFrequencies = config.donationFrequencies.filter((freq) => freq.isVisible)

  // show frequency select if there are more than one recurring frequency options
  // and the currently selected frequency is not one-time
  const shouldShowRecurringSelect =
    config.donationFrequencies.filter((freq) => freq.isVisible && freq.name !== FREQUENCY.ONE_TIME)
      .length > 1 && selectedFrequency !== FREQUENCY.ONE_TIME

  const date = new Date()

  const shouldUseRecurringEndDate =
    !config.hideRecurringEndDate && selectedFrequency !== FREQUENCY.ONE_TIME

  useEffect(() => {
    if (onFrequencyChange) {
      onFrequencyChange(selectedFrequency)
    }
  }, [selectedFrequency, onFrequencyChange])

  useEffect(() => {
    if (onRecurringEndDateChange) {
      onRecurringEndDateChange(recurringEndDate)
    }
  }, [recurringEndDate, onRecurringEndDateChange])

  const [customAmount, setCustomAmount] = useState<number | null>(order.amount.value)
  const [selectedCurrency, setSelectedCurrency] = useState<string>(currencies.default)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isFocused, setIsFocused] = useState<boolean>(false)

  const lastTrackedFrequency = useRef<string>(selectedFrequency)
  const lastTrackedAmount = useRef<number | null>(selectedAmount)
  const initialFocusRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Focus modal's first element when the component mounts
    if (initialFocusRef.current) {
      initialFocusRef.current.focus()
    }
  }, [])

  useEffect(() => {
    setErrorMessage(null)
  }, [selectedFrequency])

  // Memoized logic to switch between donationAmounts and recurringAmounts based on frequency
  const amounts = useMemo(() => {
    return selectedFrequency === FREQUENCY.ONE_TIME
      ? config.donationAmounts
      : config.recurringAmounts
  }, [selectedFrequency, config.donationAmounts, config.recurringAmounts])

  // Filter out the "other" option from the amounts to be displayed
  const originalDisplayAmounts = amounts
    .filter(({ name }) => name !== 'other')
    .filter((amount) => amount.isVisible)

  const [displayAmounts, setDisplayAmounts] = useState(originalDisplayAmounts)

  // Track frequency change if needed
  const trackFrequencyChange = (fromFrequency: string, toFrequency: string) => {
    if (fromFrequency !== toFrequency) {
      analytics.track('studio_form_frequency_change', {
        from_frequency: fromFrequency,
        to_frequency: toFrequency,
      })

      lastTrackedFrequency.current = toFrequency
    }
  }

  // Track amount change if needed
  const trackAmountChange = (fromAmount: number | null, toAmount: number | null) => {
    if (fromAmount !== toAmount) {
      analytics.track('studio_form_amount_change', {
        from_amount: fromAmount,
        to_amount: toAmount,
      })

      lastTrackedAmount.current = toAmount
    }
  }

  // Handle amount click (preset amounts)
  const handleAmountClick = (amount: number) => {
    trackAmountChange(lastTrackedAmount.current, amount)
    setSelectedAmount(amount)
    setCustomAmount(isIntelligentAsksEnabled ? null : amount)
    setErrorMessage(null)

    if (onAmountChange) {
      onAmountChange(amount)
    }
  }

  // Handle custom amount changes
  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = parseFloat(e.target.value || '0')

    if (amount > 0) {
      setErrorMessage(null)
    }

    trackAmountChange(lastTrackedAmount.current, amount) // Track the custom amount change
    setCustomAmount(amount)
    setSelectedAmount(0)

    if (onAmountChange) {
      onAmountChange(amount)
    }
  }

  const validateCustomAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFocused(false)
    const amount = parseFloat(e.target.value)

    if (amount > 10000000 || amount < config.minimumDonationAmount) {
      setErrorMessage(
        t('DonationForm.customAmountRangeError', {
          defaultValue: 'Amount must be between {{ minValue }} and 10,000,000.',
          minValue: config.minimumDonationAmount,
        }),
      )

      trackAmountChange(lastTrackedAmount.current, null)
      setCustomAmount(null)
      setSelectedAmount(0)

      if (onAmountChange) {
        onAmountChange(0)
      }
    }
  }

  const getErrorMessage = () => {
    const isGridDisplayed = displayAmounts.length > 0
    const isCustomAmountDisplayed = customAmountOption?.isVisible

    const isAmountSelected = selectedAmount > 0
    const isCustomAmountEntered = customAmount != null && customAmount > 0

    if (isGridDisplayed && !isCustomAmountDisplayed) {
      if (!isAmountSelected) {
        return t('DonationForm.selectAmountError', 'Please select an amount.')
      }
    } else if (!isGridDisplayed && isCustomAmountDisplayed) {
      if (!isCustomAmountEntered) {
        return t('DonationForm.enterAmountError', 'Please enter an amount.')
      }
    } else if (isGridDisplayed && isCustomAmountDisplayed) {
      if (!isAmountSelected && !isCustomAmountEntered) {
        return t('DonationForm.selectOrEnterAmountError', 'Please select or enter an amount.')
      }
    }

    // Added customAmount validation
    if (isCustomAmountEntered) {
      const amount = customAmount as number

      if (amount < config.minimumDonationAmount || amount > 10000000) {
        return t('DonationForm.customAmountRangeError', {
          defaultValue: 'Amount must be between {{ minValue }} and 10,000,000.',
          minValue: config.minimumDonationAmount,
        })
      }
    }

    return null
  }

  const handleSubmit = useCallback(() => {
    const error = getErrorMessage()

    if (error) {
      setErrorMessage(error)
      return
    }

    // Get the amount to submit based on the customAmount or selectedAmount
    // customAmount takes precedence
    const amountToSubmit = customAmount || selectedAmount

    onSubmit({
      frequency: selectedFrequency,
      amount: amountToSubmit,
    })
  }, [customAmount, selectedAmount, selectedFrequency, onSubmit])

  const backgroundColor = `rgba(${config.cssBackgroundColor}, ${config.cssBackgroundColorOpacity})`
  const borderRadius = getBorderRadius(config.borderRadius)
  const borderRadiusAmount = getBorderRadiusAmount(config.borderRadius)
  const boxShadow = config.dropShadow ? '0 8px 16px rgba(0,0,0,0.1)' : 'none'
  // Find "other" option for custom amounts visibility
  const customAmountOption = amounts.find(({ name }) => name === 'other')

  const primaryColor = config.theme['--theme-color__brand-primary'] || ''

  const secondaryColor = primaryColor
    ? isHexColor(primaryColor)
      ? convertRgbToRGBA(convertHexToRGB(primaryColor), 0.1)
      : convertRgbToRGBA(primaryColor, 0.1)
    : ''

  useEffect(() => {
    order.currency.exchangeRate !== 1 ? updatePrices() : resetPrices()
  }, [order.currency.exchangeRate])

  useEffect(() => {
    // If the order amount matches one of the preset amounts,
    // set it as the selected, otherwise set it as the custom amount
    if (displayAmounts.some((option) => option.amount === order.amount.value)) {
      setSelectedAmount(order.amount.value)
    } else {
      setCustomAmount(order.amount.value)
    }
  }, [displayAmounts])

  const resetPrices = () => {
    setDisplayAmounts(originalDisplayAmounts)
  }

  const updatePrices = () => {
    const newDisplayAmounts = originalDisplayAmounts.map((amount) => ({
      ...amount,
      amount: Number(
        (order.currency.exchangeRate * amount.amount).toFixed(
          getCurrencyDecimalPlaces(selectedCurrency),
        ),
      ),
    }))

    const getDefaultAmount = (amounts: typeof newDisplayAmounts): number | undefined => {
      const defaultAmount = amounts.find((amount) => amount.isDefault)

      return defaultAmount ? defaultAmount.amount : undefined
    }

    const defaultAmount = getDefaultAmount(newDisplayAmounts)

    // Prevent both custom and default amounts from being set simultaneously.
    // This happens when client goes back to amount selection after setting a custom amount.
    if (!customAmount || customAmount <= 0) {
      if (!isIntelligentAsksEnabled) order.amount.setValue(defaultAmount ?? 0)
    }

    setDisplayAmounts(newDisplayAmounts)
  }

  useEffect(() => {
    // Only make a new call to the Currency API if the currency has changed
    if (order.currency.value === selectedCurrency) return

    updatePrices()
    order.currency.setValue(selectedCurrency)
    order.currency.updateExchangeRate(selectedCurrency)
  }, [selectedCurrency, selectedFrequency])

  useEffect(() => {
    updatePrices()
  }, [amounts])

  const customStyles = (secondaryColor: string) => {
    return {
      border: 'none',
      background: `${secondaryColor}`,
      boxShadow: 'none',
      width: '74px',
      marginRight: '5px',
    }
  }

  return (
    <DonationFormContainer
      backgroundColor={backgroundColor}
      borderRadius={borderRadius}
      boxShadow={boxShadow}
    >
      <FormContentWrapper>
        {config.headingLogoUrl && (
          <HeadingLogo>
            <Image
              fill
              priority={true}
              src={config.headingLogoUrl}
              alt={t('DonationForm.headingLogoAlt', {
                defaultValue: '{{ altText }} logo',
                altText: config?.headingLogoAlt || '',
              })}
              objectFit="contain"
            />
          </HeadingLogo>
        )}

        <FrequencyFieldset
          aria-label={t('DonationForm.frequencyAriaLabel', { defaultValue: 'Donation frequency' })}
        >
          <FrequencyToggleGroup
            borderRadius={borderRadius}
            initialFocusRef={initialFocusRef}
            onFrequencyChange={(freq) => {
              trackFrequencyChange(lastTrackedFrequency.current, freq)
              setSelectedFrequency(freq)
            }}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            selectedFrequency={selectedFrequency}
            visibleFrequencies={visibleFrequencies}
            showRecurringUpsellTag={config.showRecurringUpsell}
            isLoading={isLoadingAmounts}
          />

          <SubHeading>
            {t('DonationForm.chooseAmount', 'Choose an amount to donate')}
            {selectedFrequency !== FREQUENCY.ONE_TIME && (
              <span>
                &nbsp;
                {t(
                  `DonationForm.frequencies.${selectedFrequency}`,
                  getFrequencyString(selectedFrequency),
                ).toLowerCase()}
              </span>
            )}
          </SubHeading>

          {(shouldShowRecurringSelect || shouldUseRecurringEndDate) && (
            <Row style={{ gap: '4px' }}>
              {shouldShowRecurringSelect && (
                <Column>
                  <SelectInput
                    id="donation-frequency-select"
                    label={t('DonationForm.donationFrequencyFieldLabel', 'Donation frequency')}
                    value={selectedFrequency}
                    options={visibleFrequencies
                      .filter((freq) => freq.name !== FREQUENCY.ONE_TIME)
                      .map((freq) => ({
                        label: FREQUENCY_LABELS[freq.name],
                        value: freq.name,
                      }))}
                    onChange={(frequency: Frequency) => {
                      trackFrequencyChange(lastTrackedFrequency.current, frequency)
                      setSelectedFrequency(frequency)
                    }}
                  />
                </Column>
              )}

              {shouldUseRecurringEndDate && (
                <Column>
                  <DateInput
                    id="recurring-end-date-picker"
                    label={t('DonationForm.endDateLabel', 'End date (optional)')}
                    min={date.toISOString().split('T')[0]}
                    value={recurringEndDate ?? ''}
                    onChange={(dateString: string) => {
                      if (dateString === '') {
                        setRecurringEndDate(undefined)
                      } else {
                        setRecurringEndDate(dateString)
                      }
                    }}
                  />
                </Column>
              )}
            </Row>
          )}
        </FrequencyFieldset>

        {/* Amount Selector */}
        <AmountSelectorContainer displayAmounts={displayAmounts.length}>
          {displayAmounts.map((amount) => (
            <AmountButton
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              key={amount.name}
              active={selectedAmount === amount.amount}
              borderRadius={borderRadiusAmount}
              onClick={() => handleAmountClick(amount.amount)}
              data-id={`${selectedFrequency}-amount-button-${amount.name}`}
              isLoading={isLoadingAmounts}
              aria-pressed={selectedAmount === amount.amount}
              aria-label={t('DonationForm.amountButtonAriaLabel', {
                defaultValue: 'Donation amount ${{amount}}',
                amount: amount.amount,
              })}
            >
              {formatCurrency(amount.amount, order.currency.value, i18n.language, {
                maximumFractionDigits: 0,
              })}
            </AmountButton>
          ))}
        </AmountSelectorContainer>

        {/* Custom Amount Input */}
        {customAmountOption?.isVisible && (
          <>
            <CustomAmountContainer
              active={isFocused || customAmount !== null}
              error={Boolean(errorMessage)}
              borderRadius={borderRadiusAmount}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            >
              <SelectInput
                id="donation-currency-select"
                value={selectedCurrency}
                options={currencies.options}
                onChange={(e: string) => setSelectedCurrency(e)}
                error={Boolean(errorMessage)}
                data-id="currency-input"
                customStyles={customStyles(secondaryColor)}
              />

              <CustomAmountInputContainer>
                <CustomAmountInput
                  type="number"
                  placeholder={t('DonationForm.customAmountPlaceholder', '$ Other')}
                  value={customAmount || ''}
                  onChange={handleCustomAmountChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={validateCustomAmount}
                  data-id="other-amount-input"
                />
              </CustomAmountInputContainer>
            </CustomAmountContainer>
          </>
        )}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </FormContentWrapper>

      <Spacer />

      <FlowTwoColumnStepLayout.Footer style={{ padding: '0px' }}>
        <FlowTwoColumnStepLayout.FooterButton
          type="submit"
          dataId="donation-step-continue-button"
          onClick={handleSubmit}
          disabled={!!errorMessage}
        >
          {t('DonationForm.donateButtonText', 'Donate')}
          {selectedFrequency !== FREQUENCY.ONE_TIME && (
            <span>
              {t(
                `DonationForm.frequencies.${selectedFrequency}`,
                getFrequencyString(selectedFrequency),
              )}
            </span>
          )}
        </FlowTwoColumnStepLayout.FooterButton>
      </FlowTwoColumnStepLayout.Footer>
    </DonationFormContainer>
  )
}

export default DonationForm
